// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/execution-plans/ExecutionPlanTab.jsx

import React from "react";
import ExecutionSummaryCard, { EmptyPanel, bodyTextStyle, displayValue } from "./ExecutionSummaryCard";
import ExecutionChecklist from "./ExecutionChecklist";
import PrerequisiteCard from "./PrerequisiteCard";
import OperationalPlaybook from "./OperationalPlaybook";
import RollbackPlanCard from "./RollbackPlanCard";
import BusinessImpactCard from "./BusinessImpactCard";
import ApprovalPackageCard from "./ApprovalPackageCard";

function planFromContext(context) {
  if (!context || typeof context !== "object") return null;
  return context.plan || context.execution_plan || context;
}

export default function ExecutionPlanTab({
  task,
  executionPlanContext = null,
  loading = false,
  error = "",
  onRefresh,
}) {
  const plan = planFromContext(executionPlanContext);

  if (loading) {
    return <EmptyPanel title="Loading execution plan" message="OASYS is preparing the advisory execution plan for this task." />;
  }

  if (error) {
    return (
      <EmptyPanel
        title="Execution plan unavailable"
        message={error}
      />
    );
  }

  if (!plan) {
    return (
      <EmptyPanel
        title="No execution plan loaded"
        message="Use Refresh Plan after selecting a remediation task, or reopen the modal once task context has loaded."
      />
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{
        border: "1px solid rgba(34, 197, 94, 0.22)",
        borderRadius: 18,
        background: "linear-gradient(135deg, rgba(6, 78, 59, 0.28), rgba(15, 23, 42, 0.78))",
        padding: 16,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <strong style={{ color: "#dcfce7", display: "block", marginBottom: 4 }}>Advisory Execution Plan</strong>
            <p style={bodyTextStyle}>This plan prepares the operator. It does not execute remediation, approve changes, schedule windows, run validation, or mutate workflow state.</p>
          </div>
          {onRefresh ? (
            <button
              type="button"
              onClick={onRefresh}
              style={{
                border: "1px solid rgba(148, 163, 184, 0.32)",
                borderRadius: 999,
                background: "rgba(15, 23, 42, 0.78)",
                color: "#eaf0ff",
                fontSize: 12,
                fontWeight: 850,
                padding: "8px 12px",
                cursor: "pointer",
              }}
            >
              Refresh Plan
            </button>
          ) : null}
        </div>
      </div>

      <ExecutionSummaryCard plan={plan} />
      <PrerequisiteCard prerequisites={plan.prerequisites} />
      <ExecutionChecklist steps={plan.execution_sequence} />
      <OperationalPlaybook playbook={plan.playbook} />
      <RollbackPlanCard rollbackPlan={plan.rollback_plan} />
      <BusinessImpactCard impact={plan.business_impact} />
      <ApprovalPackageCard approvalPackage={plan.approval_package} />

      <div style={{
        border: "1px solid rgba(148, 163, 184, 0.16)",
        borderRadius: 18,
        background: "rgba(2, 6, 23, 0.28)",
        padding: 16,
      }}>
        <strong style={{ color: "#f8fafc", display: "block", marginBottom: 6 }}>Selected Task</strong>
        <p style={bodyTextStyle}>
          {displayValue(task?.title || plan?.task?.title, "Task")} · {displayValue(task?.rule_id || plan?.task?.rule_id, "Rule")} · {displayValue(task?.resource_id || plan?.task?.resource_id, "Resource")}
        </p>
      </div>
    </div>
  );
}
